var _excluded = ["inline", "autoplay", "landscapeAdaptiveSource", "landscapePosterImageProps", "orientation", "portraitAdaptiveSource", "portraitPosterImageProps"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) { try { var info = gen[key](arg); var value = info.value; } catch (error) { reject(error); return; } if (info.done) { resolve(value); } else { Promise.resolve(value).then(_next, _throw); } }
function _asyncToGenerator(fn) { return function () { var self = this, args = arguments; return new Promise(function (resolve, reject) { var gen = fn.apply(self, args); function _next(value) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value); } function _throw(err) { asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err); } _next(undefined); }); }; }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
/**
 * @name AdaptiveVideoSourceProvider
 * @description Extends video to support adaptive video using HLS so you can provide an .m3u8 playlist file.
 * Determines whether to load the default video or the portrait variant based on screen orientation.
 * Loads the hls.js polyfill when needed for browsers that don't natively support HLS.
 * https://github.com/video-dev/hls.js/
 */

import React, { useCallback, useEffect, useRef, forwardRef } from 'react';
import { PropTypes } from 'prop-types';
import Video from './video';
var AdaptiveVideoSourceProvider = /*#__PURE__*/forwardRef((_ref, ref
// eslint-disable-next-line sonarjs/cognitive-complexity
) => {
  var {
      inline,
      autoplay,
      landscapeAdaptiveSource,
      landscapePosterImageProps,
      orientation,
      portraitAdaptiveSource,
      portraitPosterImageProps
    } = _ref,
    playerProps = _objectWithoutProperties(_ref, _excluded);
  var source = orientation === 'landscape' ? landscapeAdaptiveSource || portraitAdaptiveSource : portraitAdaptiveSource || landscapeAdaptiveSource;
  var hls = useRef();
  var initHlsPolyfill = useCallback(video => {
    if (!video) return;
    // Check if browser supports native HLS
    if (!video.canPlayType('application/vnd.apple.mpegurl')) {
      (function () {
        var _addHlsPolyfill = _asyncToGenerator(function* () {
          // Light polyfill may not support captions
          var Hls = (yield import('hls.js/dist/hls.light.js')).default;
          /*
           * Check if browser supports Media Source Extensions (MSE);
           * Required for the polyfill to function.
           */
          if (Hls.isSupported()) {
            var config = {
              capLevelToPlayerSize: true,
              startLevel: -1,
              testBandwidth: true
            };
            hls.current = new Hls(config);
            hls.current.attachMedia(video);
            hls.current.loadSource(source);
            hls.current.on(Hls.Events.BUFFER_CREATED, () => {
              if (!autoplay) {
                /* Prevent click-to-play videos from preloading */
                hls.current.stopLoad();
              }
            });
          }
        });
        function addHlsPolyfill() {
          return _addHlsPolyfill.apply(this, arguments);
        }
        return addHlsPolyfill;
      })()();
    }
  }, [autoplay, source]);
  useEffect(() => {
    return () => {
      if (hls.current) {
        /* Clean-up and prevent videos downloading when component unmounts */
        hls.current.stopLoad();
        hls.current.destroy();
      }
    };
  }, []);
  var triggerVideoLoad = useCallback(() => {
    if (hls.current) {
      /* Trigger video to load when Play button is clicked */
      hls.current.startLoad();
    }
  }, []);
  var posterImageProps = orientation === 'landscape' ? landscapePosterImageProps || portraitPosterImageProps : portraitPosterImageProps || landscapePosterImageProps;
  return source && /*#__PURE__*/React.createElement(Video, _extends({
    ref: ref,
    inline: inline,
    autoplay: autoplay,
    isAdaptiveSource: !!source,
    onPlayBtnClick: triggerVideoLoad,
    onVideoRefInit: initHlsPolyfill,
    posterImageProps: posterImageProps,
    source: source
  }, playerProps));
});
AdaptiveVideoSourceProvider.propTypes = {
  inline: PropTypes.bool,
  autoplay: PropTypes.bool,
  landscapeAdaptiveSource: function (props, propName) {
    var invalidPortraitAdaptive = props['portraitAdaptiveSource'] === undefined || typeof props['portraitAdaptiveSource'] !== 'string';
    var invalidLandscapeAdaptive = props[propName] === undefined || typeof props[propName] !== 'string';
    if (invalidLandscapeAdaptive || invalidPortraitAdaptive) {
      // TODO: Change this to throw an error in future ticket DAISY-568
      return null;
    }
  },
  landscapePosterImageProps: PropTypes.shape({
    alt: PropTypes.string,
    src: PropTypes.string.isRequired
  }),
  orientation: PropTypes.string,
  portraitAdaptiveSource: function (props, propName) {
    var invalidLandscapeAdaptive = props['landscapeAdaptiveSource'] === undefined || typeof props['landscapeAdaptiveSource'] !== 'string';
    var invalidPortraitAdaptive = props[propName] === undefined || typeof props[propName] !== 'string';
    if (invalidLandscapeAdaptive || invalidPortraitAdaptive) {
      // TODO: Change this to throw an error in future ticket DAISY-568
      return null;
    }
  },
  portraitPosterImageProps: PropTypes.shape({
    alt: PropTypes.string,
    src: PropTypes.string.isRequired
  })
};
AdaptiveVideoSourceProvider.defaultProps = {
  inline: true,
  autoplay: false,
  landscapeAdaptiveSource: null,
  landscapePosterImageProps: null,
  orientation: 'landscape',
  portraitAdaptiveSource: null,
  portraitPosterImageProps: null
};
export default AdaptiveVideoSourceProvider;